import { css } from '@emotion/css';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import Skeleton from 'react-loading-skeleton';

import { dateTime, type GrafanaTheme2, type IconName } from '@grafana/data';
import { t } from '@grafana/i18n';
import {
  Alert,
  Avatar,
  Box,
  Button,
  ButtonGroup,
  Dropdown,
  Field,
  Input,
  Menu,
  ScrollContainer,
  Stack,
  TagsInput,
  Text,
  TextArea,
  useStyles2,
} from '@grafana/ui';
import { useCreateQueryMutation, useUpdateQueryMutation } from 'app/extensions/api/clients/queries/v1';
import { useQueryLibraryContext } from 'app/features/explore/QueryLibrary/QueryLibraryContext';
import { type SavedQuery } from 'app/features/explore/QueryLibrary/types';

import { DrilldownExtensionPoint } from '../../../features/explore/extensions/DrilldownExtensionPoint';
import { DeleteSavedQueriesModal } from '../DeleteSavedQueriesModal';
import { QueryLibraryInteractions, dirtyFieldsToAnalyticsObject } from '../QueryLibraryAnalyticsEvents';
import { selectors } from '../e2e-selectors/selectors';
import { type QueryDetails } from '../types';
import { canEditQuery } from '../utils/identity';
import { convertAddQueryTemplateCommandToDataQuerySpec } from '../utils/mappers';
import { onOpenInExplore, type OpenInExploreOptions } from '../utils/navigation';
import { hasUnresolvedVariables } from '../utils/templateVariables';
import { useDatasource } from '../utils/useDatasource';

import { SavedQueriesCloseGuard } from './SavedQueriesCloseGuard';
import { SavedQueryDetailActions } from './SavedQueryDetailActions';
import { applyTemplateVariableOverrides, SavedQueryVariableAdjuster } from './SavedQueryVariableAdjuster';

/**
 * Analytics/behaviour context used when creating a brand-new saved query from the Explore banner.
 * QueryLibraryEditingHeader sets this as the ambient render context in add mode, so the drawer it opens
 * inherits it.
 */
export const EXPLORE_NEW_SAVED_QUERY_CONTEXT = 'explore-new-saved-query';

/**
 * A save action for the create flow's split Save button. `onAfterSave` runs after the create
 * mutation resolves, receiving the new query's uid — used to drive post-save navigation.
 */
export type SavedQuerySaveAction = {
  key: string;
  label: string;
  icon?: IconName;
  testId?: string;
  onAfterSave: (uid: string) => void;
};

type Props = {
  query: SavedQuery;
  onTitleChange?: (title: string | undefined) => void;
  onDelete?: () => void;
  onSaveNew?: (uid: string) => void;
  /**
   * When provided (create flow only), the footer renders a split Save button whose primary action is
   * the first entry and whose dropdown lists the rest. The chosen action's `onAfterSave` runs after
   * the create succeeds. When omitted, the footer keeps the default single Save button.
   */
  saveActions?: SavedQuerySaveAction[];
  /**
   * Mounted as a dedicated edit drawer rather than the detail pane of the library drawer: the form is
   * always in edit mode, so there is no read-only view for Cancel to fall back to and it closes instead.
   */
  standalone?: boolean;
  /**
   * Where "Edit in Explore" should send the user back to. Built by the owning page, which owns the
   * `?editQuery` contract that reopens this drawer on return.
   */
  savedQueryReturnTo?: string;
  isDuplicate?: boolean;
  /**
   * Called when Duplicate is clicked. Must be owned above this panel: Duplicate clears the uid and
   * remounts this component via `key={uid ?? 'new-query'}`, so panel-local state cannot survive.
   */
  onDuplicate?: () => void;
  /** Called when a pending create (new or duplicate) is cancelled, so parent can clear isDuplicate. */
  onCancelNew?: () => void;
};

// Full detail/edit form for a selected query: title, query display, template variable adjuster,
// metadata fields (datasource, description, tags, author, date), and a footer with action buttons.
export function SavedQueriesDetailsPanel({
  query,
  onTitleChange,
  onDelete,
  onSaveNew,
  saveActions,
  standalone,
  savedQueryReturnTo,
  isDuplicate,
  onDuplicate,
  onCancelNew,
}: Props) {
  const styles = useStyles2(getStyles);

  const {
    context,
    clearCloseGuard,
    closeDrawer,
    onSelectQuery,
    setNewQuery,
    onSave,
    setCloseGuard,
    isEditingQuery,
    setIsEditingQuery,
    openedToSaveQuery,
    templateVariableOverrides,
    setTemplateVariableOverrides,
    triggerAnalyticsEvent,
  } = useQueryLibraryContext();
  const { loading: datasourceApiLoading } = useDatasource(query.datasourceRef);
  const hasTemplateVariables = hasUnresolvedVariables(query.query);
  const isCommandPalette = context === 'command-palette';
  const [updateQuery, { isLoading: isUpdateLoading }] = useUpdateQueryMutation();
  const [createQuery, { isLoading: isCreateLoading }] = useCreateQueryMutation();

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    setFocus,
    formState: { isDirty, isSubmitting, dirtyFields },
  } = useForm<QueryDetails>({
    defaultValues: {
      title: query.title ?? '',
      description: query.description ?? '',
      tags: query.tags ?? [],
      isVisible: query.isVisible ?? true,
    },
  });

  const [isCloseGuardOpen, setIsCloseGuardOpen] = useState(false);
  const [isBannerDeleteOpen, setIsBannerDeleteOpen] = useState(false);
  // Ref used to restore focus to the Edit button after the close guard is dismissed via Discard.
  // This keeps focus inside the modal so useDismiss retains control of Esc and its stopPropagation
  // prevents keybindingSrv's global Esc handler from firing exit() and closing panel-edit.
  const editButtonRef = useRef<HTMLButtonElement>(null);
  // Pending Explore-open request captured when the dirty-form guard blocked navigation. On Discard
  // confirm we replay this so the user still lands in Explore with the same params.
  const pendingExploreOpenRef = useRef<{ query: SavedQuery; options?: OpenInExploreOptions } | null>(null);

  // No uid means this is a pending create — the form was opened via "Save query" before any API call
  const isNew = !query.uid;
  const isSaving = isSubmitting || isUpdateLoading || isCreateLoading;
  // Metadata failed to load (e.g. the datasource was deleted). The row stays visible so it can be deleted,
  // but it can't be edited or selected since its datasource-derived fields are missing.
  const hasLoadError = Boolean(query.loadError);
  const canEdit = canEditQuery(query) && !hasLoadError;
  const canDelete = canEditQuery(query);
  const titleValue = watch('title');

  // Focus the title input whenever editing starts.
  // setTimeout defers past FloatingFocusManager's initial-focus effect so we win the race
  // when the modal and panel mount in the same render cycle (e.g. reopening with newQuery already set).
  useEffect(() => {
    if (!isEditingQuery) {
      return;
    }

    const timer = window.setTimeout(() => setFocus('title'), 0);
    return () => window.clearTimeout(timer);
  }, [isEditingQuery, setFocus]);

  // Sync the live title up to the list so the card updates while the user types.
  // Passing undefined when not editing intentionally clears the override so the card
  // falls back to the last-saved title.
  useEffect(() => {
    onTitleChange?.(isEditingQuery ? titleValue : undefined);
  }, [titleValue, isEditingQuery, onTitleChange]);

  // Reset template variable overrides whenever the selected query changes.
  useEffect(() => {
    setTemplateVariableOverrides({});
  }, [query.uid, setTemplateVariableOverrides]);

  const formattedDate = dateTime(query.createdAtTimestamp).format('ddd MMM DD YYYY HH:mm [GMT]ZZ');

  const onSubmit = async (data: QueryDetails, action = saveActions?.[0]) => {
    if (query.uid) {
      triggerAnalyticsEvent(QueryLibraryInteractions.saveEditClicked, dirtyFieldsToAnalyticsObject(dirtyFields));
      await updateQuery({ name: query.uid, patch: { spec: { ...data } } }).unwrap();
      if (context === 'unknown') {
        // "unknown" context means the modal was opened outside of apps like Explore, Panel-Editor, etc.
        // Usually means Query History. This will eventually be removed once Query History / Recent tab is handled.
        closeDrawer();
        onSave?.();
      } else {
        reset(data);
        setIsEditingQuery(false);
        onSave?.();
      }
    } else {
      const result = await createQuery({
        query: convertAddQueryTemplateCommandToDataQuerySpec({
          title: data.title || t('saved-queries.details.default-title', 'New query'),
          description: data.description,
          isVisible: data.isVisible,
          tags: data.tags,
          targets: [query.query],
        }),
      }).unwrap();

      triggerAnalyticsEvent(QueryLibraryInteractions.saveQuerySuccess, {
        datasourceType: query.datasourceType,
        hasTemplateVariables,
        isDuplicate: Boolean(isDuplicate),
        // Which of the split Save actions was chosen (undefined when the footer has a plain Save button),
        // so "stay in Explore" can be told apart from "navigate away".
        saveAction: action?.key,
      });

      const uid = result.metadata?.name ?? '';
      setNewQuery(undefined);
      setIsEditingQuery(false);
      onSave?.();
      onSaveNew?.(uid);
      if (context === 'unknown') {
        closeDrawer();
      }
      // Runs the chosen split-button action (e.g. navigate to the list, or stay in Explore). Last so
      // the form/context state is already reset before any navigation happens.
      action?.onAfterSave(uid);
    }
  };

  // Shared reset handler used by both the cancel button and the discard confirmation modal.
  // For new queries, clears the pending newQuery from context so the card disappears from the list.
  // Standalone skips cancel* analytics: EditQueryDrawer.guardedClose already reports
  // editDrawerClosedWithoutSaving / duplicateDrawerClosedWithoutSaving on the subsequent closeDrawer,
  // and firing both would double-count one abandon.
  const resetForm = useCallback(() => {
    if (isNew) {
      if (!standalone) {
        triggerAnalyticsEvent(QueryLibraryInteractions.cancelSaveNewQueryClicked);
      }
      setNewQuery(undefined);
      onCancelNew?.();
    } else if (!standalone) {
      triggerAnalyticsEvent(QueryLibraryInteractions.cancelEditClicked);
    }
    reset();
    setIsEditingQuery(false);
  }, [isNew, standalone, triggerAnalyticsEvent, setNewQuery, onCancelNew, reset, setIsEditingQuery]);

  // Intercept modal close when there are unsaved edits. Skipped for 'unknown' context because
  // cancel already closes the modal there, so the guard would never fire.
  const willBlockClose = isEditingQuery && isDirty && context !== 'unknown';

  useEffect(() => {
    setCloseGuard(() => {
      if (willBlockClose) {
        setIsCloseGuardOpen(true);
        return false;
      }
      return true;
    });
    return () => {
      setCloseGuard(() => true);
    };
  }, [willBlockClose, setCloseGuard]);

  // Attempt to open Explore. Only remember a pending retry when the guarded close actually blocks
  // (dirty form). Always stashing first would leave a stale pending after the multi-query replace
  // modal is cancelled, so a later Discard would incorrectly navigate to Explore.
  const tryOpenInExplore = useCallback(
    (queryToOpen: SavedQuery, options?: OpenInExploreOptions) => {
      pendingExploreOpenRef.current = null;
      onOpenInExplore(
        queryToOpen,
        (isSelectingQuery?, closedToEditInExplore?) => {
          const closed = closeDrawer(isSelectingQuery, closedToEditInExplore);
          if (closed === false) {
            pendingExploreOpenRef.current = { query: queryToOpen, options };
          }
          return closed;
        },
        options
      );
    },
    [closeDrawer]
  );

  const onCancel = () => {
    if (standalone) {
      // The drawer has no view mode to return to, so Cancel closes it. guardedClose opens the discard
      // guard when the form is dirty, otherwise closes immediately.
      // Do not report cancel* here: guardedClose (and the Discard → resetForm → closeDrawer path)
      // already emit editDrawerClosedWithoutSaving / duplicateDrawerClosedWithoutSaving.
      closeDrawer();
      return;
    }
    if (isDirty && context !== 'unknown') {
      setIsCloseGuardOpen(true);
      return;
    }
    resetForm();
    if (context === 'unknown' || openedToSaveQuery) {
      closeDrawer();
    }
  };

  return (
    <>
      <form
        className={styles.panel}
        onSubmit={handleSubmit((data) => onSubmit(data))}
        aria-label={t('saved-queries.details.panel-aria-label', 'Query details: {{title}}', {
          title: query.title ?? '',
        })}
      >
        <div role="status" className={styles.srOnly}>
          {isEditingQuery ? t('saved-queries.details.editing-mode', 'Editing query') : ''}
        </div>
        <ScrollContainer overflowX="hidden">
          <Box paddingTop={1} paddingX={1}>
            {/* Header: Title */}
            <div className={styles.header}>
              {isEditingQuery ? (
                <Box flex={1}>
                  <Field label={t('saved-queries.details.name', 'Name')}>
                    <Input {...register('title')} data-testid={selectors.components.savedQueriesModal.titleInput} />
                  </Field>
                </Box>
              ) : (
                <Box flex={1} minWidth={0}>
                  <Text element="h5" truncate>
                    {query.title ?? ''}
                  </Text>
                </Box>
              )}
            </div>

            {hasLoadError && (
              <Box marginBottom={2}>
                <Alert
                  severity="error"
                  title={t(
                    'saved-queries.details.load-error-title',
                    "The data source for this query may have been deleted. This query can't be used - create a new saved query."
                  )}
                  onRemove={canDelete ? () => setIsBannerDeleteOpen(true) : undefined}
                  buttonContent={canDelete ? t('saved-queries.details.delete', 'Delete') : undefined}
                />
                <DeleteSavedQueriesModal
                  isOpen={isBannerDeleteOpen}
                  uids={query.uid ? [query.uid] : []}
                  modalClass={styles.centeredModal}
                  onDismiss={() => setIsBannerDeleteOpen(false)}
                  onDeleted={(_deletedUids, allDeleted) => {
                    if (!allDeleted) {
                      return;
                    }
                    setIsBannerDeleteOpen(false);
                    triggerAnalyticsEvent(QueryLibraryInteractions.deleteQueryClicked);
                    onDelete?.();
                  }}
                />
              </Box>
            )}

            {/* Query display */}
            <Box marginBottom={2}>
              {datasourceApiLoading ? (
                <Box marginY={2}>
                  <Skeleton height={48} />
                </Box>
              ) : (
                // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                <code className={styles.queryCode} tabIndex={0}>
                  {query.queryText || JSON.stringify(query.query, null, 2)}
                </code>
              )}
              {(context === 'explore' || standalone) && query.uid && !hasLoadError && (
                <Box marginTop={0.5}>
                  <Button
                    size="sm"
                    variant="success"
                    fill="text"
                    type="button"
                    data-testid={selectors.components.savedQueriesModal.editInExploreButton}
                    onClick={() => {
                      triggerAnalyticsEvent(QueryLibraryInteractions.editInExploreClicked, {
                        datasourceType: query.datasourceType,
                      });
                      tryOpenInExplore(query, savedQueryReturnTo ? { savedQueryReturnTo } : undefined);
                    }}
                  >
                    {t('saved-queries.details.edit-in-explore', 'Edit in Explore')}
                  </Button>
                </Box>
              )}
              {hasTemplateVariables && !isEditingQuery && (
                <Box marginTop={2}>
                  <SavedQueryVariableAdjuster
                    query={query}
                    overrides={templateVariableOverrides}
                    onChangeOverrides={setTemplateVariableOverrides}
                  />
                </Box>
              )}
            </Box>

            {/* Details fields */}
            <Field
              label={t('saved-queries.details.datasource', 'Data source')}
              invalid={hasLoadError}
              error={
                hasLoadError ? t('saved-queries.details.datasource-not-found', 'Data source not found') : undefined
              }
            >
              <Input readOnly invalid={hasLoadError} value={query.datasourceName ?? query.datasourceType ?? ''} />
            </Field>

            <Field label={t('saved-queries.details.description', 'Description')}>
              <TextArea
                readOnly={!isEditingQuery}
                rows={3}
                {...register('description')}
                data-testid={selectors.components.savedQueriesModal.descriptionInput}
              />
            </Field>

            <Field label={t('saved-queries.details.tags', 'Tags')} htmlFor="tags">
              <Controller
                name="tags"
                control={control}
                defaultValue={query.tags ?? []}
                render={({ field: { ref, value, onChange, ...field } }) => (
                  <TagsInput
                    {...field}
                    id="tags"
                    disabled={!isEditingQuery}
                    onChange={(tags) => onChange(Array.from(new Set(tags)).sort())} // deduplicate + sort
                    tags={value ? Array.from(value) : []}
                    autoColors={false}
                  />
                )}
              />
            </Field>

            <Field label={t('saved-queries.details.author', 'Author')}>
              <Input
                readOnly
                prefix={
                  query.user?.avatarUrl ? (
                    <Box marginRight={0.5}>
                      <Avatar width={2} height={2} src={query.user.avatarUrl} alt="" />
                    </Box>
                  ) : undefined
                }
                value={query.user?.displayName ?? ''}
              />
            </Field>

            <Field label={t('saved-queries.details.date-added', 'Date added')}>
              <Input readOnly value={formattedDate} />
            </Field>
          </Box>
        </ScrollContainer>

        {/* Footer: Duplicate/Delete on left, Edit/Save/Cancel on right */}
        <div className={styles.footer}>
          {!isCommandPalette && (
            // The standalone edit drawer gets no duplicate/delete: it is opened from
            // surfaces that already offer both right next to Edit, and deleting the
            // query you are editing from inside the editor is a footgun. The wrapper
            // keeps space-between right-aligning the Save/Cancel group.
            <div>
              {!standalone && (
                <SavedQueryDetailActions
                  query={query}
                  disabled={isEditingQuery || isNew}
                  onDelete={onDelete}
                  onDuplicate={onDuplicate}
                />
              )}
            </div>
          )}
          {isEditingQuery ? (
            <Stack key="edit-mode" gap={1} alignItems="center">
              <Button variant="secondary" size="sm" onClick={onCancel} type="button">
                {t('saved-queries.details.cancel', 'Cancel')}
              </Button>
              {saveActions?.length ? (
                <ButtonGroup>
                  <Button
                    variant="primary"
                    size="sm"
                    type="submit"
                    disabled={isSaving}
                    icon={isSaving ? 'spinner' : undefined}
                    data-testid={selectors.components.savedQueriesModal.saveButton}
                  >
                    {saveActions[0].label}
                  </Button>
                  <Dropdown
                    placement="bottom-end"
                    overlay={
                      <Menu>
                        {saveActions.slice(1).map((saveAction) => (
                          <Menu.Item
                            key={saveAction.key}
                            label={saveAction.label}
                            icon={saveAction.icon}
                            testId={saveAction.testId}
                            onClick={handleSubmit((data) => onSubmit(data, saveAction))}
                          />
                        ))}
                      </Menu>
                    }
                  >
                    <Button
                      variant="primary"
                      size="sm"
                      icon="angle-down"
                      disabled={isSaving}
                      aria-label={t('saved-queries.details.save-options-aria', 'More save options')}
                      data-testid={selectors.components.savedQueriesModal.saveMenuButton}
                    />
                  </Dropdown>
                </ButtonGroup>
              ) : (
                <Button
                  variant="primary"
                  size="sm"
                  type="submit"
                  disabled={isSaving}
                  icon={isSaving ? 'spinner' : undefined}
                  data-testid={selectors.components.savedQueriesModal.saveButton}
                >
                  {context === 'unknown'
                    ? t('saved-queries.details.save-and-close', 'Save and close')
                    : t('saved-queries.details.save', 'Save')}
                </Button>
              )}
            </Stack>
          ) : (
            <Stack key="view-mode" gap={1}>
              {query.uid && context !== 'drilldown' && !hasLoadError && (
                <DrilldownExtensionPoint
                  compact
                  queries={[query.query]}
                  onExtensionClick={() => {
                    triggerAnalyticsEvent(QueryLibraryInteractions.openInDrilldownClicked);
                  }}
                />
              )}
              <Button
                ref={editButtonRef}
                variant="secondary"
                size="sm"
                type="button"
                disabled={!canEdit}
                data-testid={selectors.components.savedQueriesModal.editButton}
                onClick={() => {
                  triggerAnalyticsEvent(QueryLibraryInteractions.editQueryClicked);
                  setIsEditingQuery(true);
                }}
              >
                {t('saved-queries.details.edit', 'Edit')}
              </Button>
              <Button
                variant="primary"
                size="sm"
                type="button"
                disabled={hasLoadError}
                data-testid={selectors.components.savedQueriesModal.selectButton}
                onClick={() => {
                  const { query: resolvedQuery, templateVariablesChanged } = applyTemplateVariableOverrides(
                    query,
                    templateVariableOverrides
                  );
                  if (isCommandPalette) {
                    // Run-only open (no editing ref); skip selectQueryClicked here (that's for
                    // selecting into a host editor). The drawer-close event tracks the Explore nav.
                    tryOpenInExplore({ ...query, query: resolvedQuery }, { setEditSavedQueryRef: false });
                  } else {
                    triggerAnalyticsEvent(QueryLibraryInteractions.selectQueryClicked, {
                      hasTemplateVariables,
                      templateVariablesChanged,
                    });
                    onSelectQuery(resolvedQuery, query.title);
                    closeDrawer(true);
                  }
                }}
              >
                {isCommandPalette
                  ? t('saved-queries.details.open-in-explore', 'Open in Explore')
                  : t('saved-queries.details.select', 'Select query')}
              </Button>
            </Stack>
          )}
        </div>
      </form>
      <SavedQueriesCloseGuard
        isOpen={isCloseGuardOpen}
        onConfirm={() => {
          setIsCloseGuardOpen(false);
          resetForm();
          const pending = pendingExploreOpenRef.current;
          pendingExploreOpenRef.current = null;
          if (pending) {
            // The user opened "Edit in Explore" (or command-palette Open in Explore) on a dirty
            // form, which triggered this guard. Clear the guard first so onOpenInExplore's
            // guarded close now succeeds and pushes the Explore URL. skipReplaceConfirm avoids
            // re-showing the multi-query modal the user already confirmed before the dirty guard.
            clearCloseGuard();
            onOpenInExplore(pending.query, closeDrawer, {
              ...pending.options,
              skipReplaceConfirm: true,
            });
          } else if (openedToSaveQuery || standalone) {
            clearCloseGuard();
            closeDrawer(false, false);
          } else {
            // Deferred so the Edit button is visible in the DOM before we focus it
            // (resetForm sets isEditingQuery → false, which re-renders view mode).
            window.setTimeout(() => editButtonRef.current?.focus(), 0);
          }
        }}
        onDismiss={() => {
          pendingExploreOpenRef.current = null;
          setIsCloseGuardOpen(false);
        }}
      />
    </>
  );
}

const getStyles = (theme: GrafanaTheme2) => ({
  panel: css({
    flex: 1,
    overflow: 'hidden',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  }),
  header: css({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: 0,
    marginBottom: theme.spacing(2),
  }),
  footer: css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: theme.spacing(1),
    borderTop: `1px solid ${theme.colors.border.weak}`,
  }),
  srOnly: css({
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0,0,0,0)',
    whiteSpace: 'nowrap',
    border: 0,
  }),
  queryCode: css({
    backgroundColor: theme.colors.action.disabledBackground,
    borderRadius: theme.shape.radius.default,
    display: 'block',
    overflowWrap: 'break-word',
    padding: theme.spacing(1),
    whiteSpace: 'pre-wrap',
    fontSize: theme.typography.bodySmall.fontSize,
  }),
  centeredModal: css({
    top: '50%',
    transform: 'translateY(-50%)',
  }),
});
